import { FC, useState } from 'react'

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Cluster,
  Grid,
  Icon,
  IconButton,
  Stack,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { copyTextToClipboard } from '@/utils/clipboard'
import { useLocalisation } from '@/utils/localisation'

const INITIAL_VISIBLE_COUNT = 6

const useText = () => {
  const { translate } = useLocalisation()
  return {
    title: translate('acknowledgementReceivers.title'),
    emailAddresses: translate('acknowledgementReceivers.emailAddresses'),
    showLess: translate('acknowledgementReceivers.showLess'),
    viewAll: (count: number) => translate('acknowledgementReceivers.viewAll', { count }),
    copy: translate('actions.copy'),
    copied: translate('details.overview.bar.notifications.copied'),
  }
}

const ReceiverEmailCell: FC<{ email: string }> = ({ email }) => {
  const text = useText()
  const toaster = useToaster()

  return (
    <Cluster gap={8} y="center">
      <Typography>{email}</Typography>
      <IconButton
        label={text.copy}
        icon="FileCopy"
        size="sm"
        onClick={async () => {
          await copyTextToClipboard(email)
          toaster({ title: text.copied })
        }}
      />
    </Cluster>
  )
}

export type AcknowledgementReceiversCardProps = {
  receivers: string[]
}

export const AcknowledgementReceiversCard: FC<AcknowledgementReceiversCardProps> = ({ receivers }) => {
  const text = useText()
  const [expanded, setExpanded] = useState(false)

  const showToggle = receivers.length > INITIAL_VISIBLE_COUNT
  const visibleReceivers = showToggle && !expanded ? receivers.slice(0, INITIAL_VISIBLE_COUNT) : receivers

  return (
    <Card>
      <CardHeader>
        <CardTitle>{text.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Stack gap={12}>
          <Typography variant="small" color="muted">
            {text.emailAddresses}
          </Typography>
          <Grid col={2} gap={16} breakpoint="sm">
            {visibleReceivers.map((email) => (
              <ReceiverEmailCell key={email} email={email} />
            ))}
          </Grid>
          {showToggle && (
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              <Cluster gap={4} y="center">
                <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} />
                {expanded ? text.showLess : text.viewAll(receivers.length)}
              </Cluster>
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
